import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Store} from "@ngrx/store";
import * as fromStore from '../../../../../../store';
import {ActivatedRoute} from "@angular/router";
import {Observable} from "rxjs";
import {filter, first, map, switchMap, takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {VerifyHostnameComponent} from "../../dialogs/verify-cloudflare-hostname/verify-hostname.component";
import {DbModalService} from "../../../../../../modules/modal/modal";
import {combineLatest} from "rxjs";
import {UserSubscription} from "../../../../../../user/user-subscription";
import {GoogleAnalyticsService} from "../../../../../../shared/services/google-analytics/google-analytics.service";
import {UserService} from "../../../../../../user/user.service";
import {DbResponse} from "../../../../../../shared/interceptors/db-response";
import {Website} from "../../../../website";
import {CloudflareData} from "../../../mappers/cloudflare/cloudflare-data";
import {Utils} from "../../../../../../shared/utils/utils";
import {getUserLocalStorage, getUserLocalStorageLoaded} from "../../../../../../store";

@Component({
    selector: 'db-cdn-general',
    templateUrl: './cdn-general.component.html',
    styleUrls: ['./cdn-general.component.scss'],
    standalone: false
})
export class CdnGeneralComponent implements OnInit, OnDestroy {

  @Input() isNameServer: boolean;
  public websiteUrl: any;
  public bandwidth: string;
  public bandwidthNumber: number;
  public paymentType: string;
  public isStatusPending: boolean;
  public websiteId: number;
  public initialLoading = true;
  public loading = false;
  public sendStatusAlready: boolean;
  public userLocalStorage: any;
  public userSubscription: UserSubscription;
  public cloudflareHostname: string;
  public isDNSCacheTooltipHidden = false;

  private readonly _destroyStream$ = new Subject<boolean>();

  constructor(
    private readonly _store: Store<fromStore.DbStoreState>,
    private readonly _route: ActivatedRoute,
    private readonly _dbModalService: DbModalService,
    private _userService: UserService,
    private _googleAnalyticsService: GoogleAnalyticsService
  ) {
  }

  ngOnInit() {
    this.websiteId = +this._route.snapshot.parent.parent.params['id'];
    this.getWebsiteUrl();
    this.getData();
    this._getUserSubscription();
    (this.cloudflareHostname !== this.websiteUrl) && this.sendEvent('Dashboard Error', 'Already using Cloudflare');
  }

  ngOnDestroy(): void {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private _getUserSubscription(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => this._store.select(fromStore.getUserSubscription)),
        first()
      )
      .subscribe((userSubscription: UserSubscription) => {
        this.userSubscription = userSubscription;
      });
  }

  private getData(): void {
    combineLatest(
      this.getBandWith(),
      this.getPaymentType(),
      this.getCloudflareData(),
      this.getUserLocalStorage()
    )
      .pipe(takeUntil(this._destroyStream$))
      .subscribe(([bandwidth, type, cloudflareData, userLocalStorage]) => {
        this.bandwidth = bandwidth && bandwidth.display;
        this.bandwidthNumber = bandwidth.value / 1024 / 1024;
        this.paymentType = type;
        this.isStatusPending = cloudflareData && cloudflareData.cfStatus === 'pending';
        this.cloudflareHostname = cloudflareData && cloudflareData.hostname;
        this.userLocalStorage = userLocalStorage;
        this.isDNSCacheTooltipHidden = userLocalStorage[`DNSCacheTooltipHidden${this.websiteId}`];
        (!this.sendStatusAlready || (this.userLocalStorage['cdnStatus' + this.websiteId] && this.userLocalStorage['cdnStatus' + this.websiteId] !== this.isStatusPending)) && this._cdnLocalStorageCheck();
        this.initialLoading = false;
        this.loading = false;
      });
  }

  private _cdnLocalStorageCheck() {
    this.userLocalStorage[`cdnStatus${this.websiteId}`] = !!this.isStatusPending;
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe((response: DbResponse): void => {
        if (response.success) {
          this._sendGAStatusEvent();
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }

  private _sendGAStatusEvent(): void {
    this.sendStatusAlready = true;
    this.sendEvent('Dashboard Status', !!this.isStatusPending ? 'Cloudflare Integration pending' : 'Cloudflare Integration activated');
  }

  private getBandWith(): Observable<any> {
    return this._store.select(fromStore.getBandwidth(this.websiteId))
      .pipe(
        filter(({bandwidth}) => !!bandwidth),
        map(({bandwidth}) => bandwidth),
      );
  }
  private getUserLocalStorage(): Observable<any> {
    return this._store.select(getUserLocalStorageLoaded)
      .pipe(
        filter(loaded => loaded),
        switchMap(() => this._store.select(getUserLocalStorage)),
        takeUntil(this._destroyStream$),
      );
  }

  private getPaymentType(): Observable<string> {
    return this._store.select(fromStore.getUserSubscription)
      .pipe(
        takeUntil(this._destroyStream$),
        filter(sbscr => !!sbscr),
        map(({paymentType}) => paymentType)
      );
  }

  private getWebsiteUrl(): void {
    this._store.select(fromStore.getWebsite(this.websiteId))
      .pipe(
        takeUntil(this._destroyStream$),
        filter(website => !!website)
      )
      .subscribe((website: Website) => {
        this.websiteUrl = website && website.site_url && Utils.replaceUrl(new URL(website.site_url).hostname);
      });
  }

  private getCloudflareData(): Observable<CloudflareData> {
    return this._store.select(fromStore.getCloudflareData(this.websiteId))
      .pipe(
        filter(({cfStatus}) => !!cfStatus),
      );
  }

  public showDnsRecords(): void {
    this.sendEvent('Dashboard Action', 'View DNS records button (Pending status tooltip)');
    const modal = this._dbModalService.open(VerifyHostnameComponent);
    modal.componentInstance.websiteId = this.websiteId;
    modal.componentInstance.websiteUrl = this.websiteUrl;

  }

  public verifyDNS(): void {
    this.sendEvent('Dashboard Action', !!this.isStatusPending ? 'Cloudflare Integration pending' : 'Cloudflare Integration activated');
    this.loading = true;
    this._store.dispatch(new fromStore.LoadCloudflareData(this.websiteId));
  }

  public openChat(): void {
    /*window['zE']('messenger', 'chat:removeTags', ['generic_support']);
    window['zE']('messenger', 'chat:addTags', ['open_livechat']);*/

    window['Smooch']?.open();
    /*window['zE']('messenger', 'chat:send', 'I need help with setting up Cloudflare');*/
    this._store.dispatch(new fromStore.ToggleChatOverlay(false));
  }

  public sendEvent(category: string, action: string): void {
    const analyticsData = {
      eventCategory: category,
      eventAction: action,
      eventLabel: 'Cloudflare',
      websiteId: this.websiteId
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }

  public hideDNSCacheTooltip(): void {
    this.isDNSCacheTooltipHidden = true;
    this.userLocalStorage[`DNSCacheTooltipHidden${this.websiteId}`] = true;
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }
}
